import { supabase } from "@/integrations/supabase/client";
import type { Property, Purpose, PropertyType } from "@/lib/mock-data";
import { fetchPublishedListings } from "@/lib/listings";

export interface DbProperty {
  id: string;
  title: string;
  description: string | null;
  purpose: Purpose;
  price: number;
  price_label: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  balcony: number | null;
  area_sqft: number | null;
  address: string | null;
  city: string | null;
  latitude: number | null;
  longitude: number | null;
  image_url: string | null;
  highlights: string[] | null;
  status: string;
  created_at?: string | null;
  property_types: { slug: PropertyType; label: string } | null;
}

const TYPE_SLUGS: PropertyType[] = [
  "house",
  "apartment",
  "villa",
  "land",
  "room",
  "office",
  "shop",
  "commercial",
  "warehouse",
];

function slugifyType(label: string | null): PropertyType {
  const s = (label ?? "").toLowerCase().replace(/\s+/g, "_");
  return TYPE_SLUGS.find((t) => s.includes(t)) ?? "house";
}

/**
 * Approved listings for the map: the dedicated Sale / Rent / Lease tables
 * plus any legacy `properties` rows, in one feed.
 */
export async function fetchPublishedProperties(): Promise<DbProperty[]> {
  const [{ data, error }, listings] = await Promise.all([
    supabase
      .from("properties")
      .select(
        "created_at,id,title,description,purpose,price,price_label,bedrooms,bathrooms,balcony,area_sqft,address,city,latitude,longitude,image_url,highlights,status,property_types(slug,label)",
      )
      .eq("status", "published")
      .order("created_at", { ascending: false }),
    fetchPublishedListings().catch(() => []),
  ]);
  if (error) throw error;
  const legacy = (data as unknown as DbProperty[]) ?? [];
  const fromListings: DbProperty[] = listings.map((l) => ({
    id: l.id,
    title: l.title,
    description: l.description,
    purpose: l.service as Purpose,
    price: l.price,
    price_label: l.price_label,
    bedrooms: l.bedrooms,
    bathrooms: l.bathrooms,
    balcony: l.balcony,
    area_sqft: l.area_sqft,
    address: l.address,
    city: l.city,
    latitude: l.latitude,
    longitude: l.longitude,
    image_url: l.image_url,
    highlights: l.highlights,
    status: "published",
    created_at: l.created_at,
    property_types: { slug: slugifyType(l.listing_type), label: l.listing_type ?? "Property" },
  }));
  return [...fromListings, ...legacy];
}

/**
 * Project real lat/lng into the faux-map viewport (%). Uses the min/max of the
 * visible set with 10% padding; falls back to an even spread when a point is
 * missing coordinates.
 */
export function projectToViewport(rows: DbProperty[]) {
  const withCoords = rows.filter((r) => r.latitude != null && r.longitude != null);
  const minLat = Math.min(...withCoords.map((r) => r.latitude as number));
  const maxLat = Math.max(...withCoords.map((r) => r.latitude as number));
  const minLng = Math.min(...withCoords.map((r) => r.longitude as number));
  const maxLng = Math.max(...withCoords.map((r) => r.longitude as number));
  const latSpan = maxLat - minLat || 0.01;
  const lngSpan = maxLng - minLng || 0.01;

  return rows.map((r, i): Property => {
    let x = 15 + ((i * 137) % 70);
    let y = 15 + ((i * 89) % 70);
    if (r.latitude != null && r.longitude != null) {
      x = 10 + ((r.longitude - minLng) / lngSpan) * 80;
      y = 90 - ((r.latitude - minLat) / latSpan) * 80; // north = up
    }
    return {
      id: r.id,
      title: r.title,
      type: (r.property_types?.slug ?? "house") as PropertyType,
      purpose: r.purpose,
      price: Number(r.price ?? 0),
      priceLabel: r.price_label ?? `₹${Number(r.price ?? 0).toLocaleString("en-IN")}`,
      location: [r.address, r.city].filter(Boolean).join(", ") || "—",
      distanceKm: 0,
      bedrooms: r.bedrooms ?? undefined,
      bathrooms: r.bathrooms ?? undefined,
      balcony: r.balcony ?? undefined,
      areaSqft: Number(r.area_sqft ?? 0),
      highlights: r.highlights ?? [],
      description: r.description ?? "",
      image:
        r.image_url ??
        "https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=800&q=70",
      x,
      y,
      lat: r.latitude ?? undefined,
      lng: r.longitude ?? undefined,
      createdAt: r.created_at ?? undefined,
    };
  });
}
